// hooks/useUserAgreements.js
import { useState, useEffect, useCallback } from "react";
import apiClient from "utils/apiClient";

const useUserAgreements = () => {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAgreements = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = "profile-service/api/v1/user-agreements";
      const response = await apiClient.get(url);
      const data = response.data;
      if (data && data.content && Array.isArray(data.content)) {
        setAgreements(data.content);
      } else if (Array.isArray(data)) {
        setAgreements(data);
      } else {
        setAgreements([]);
        console.error("API returned unexpected data structure for agreements list:", data);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching user agreements:", errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const addAgreement = useCallback(
    async (agreementData) => {
      try {
        const url = "profile-service/api/v1/user-agreements";
        const response = await apiClient.post(url, agreementData);
        fetchAgreements();
        return response.data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setError(errorMessage);
        throw err;
      }
    },
    [fetchAgreements]
  );

  const updateAgreement = useCallback(
    async (id, agreementData) => {
      if (!id) {
        throw new Error("Agreement ID is required for update.");
      }
      try {
        const url = `profile-service/api/v1/user-agreements/${id}`;
        const response = await apiClient.put(url, agreementData);
        fetchAgreements();
        return response.data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setError(errorMessage);
        throw err;
      }
    },
    [fetchAgreements]
  );

  const deleteAgreement = useCallback(
    async (id) => {
      if (!id) {
        console.error("Attempted to delete agreement with null ID.");
        return;
      }
      try {
        const url = `profile-service/api/v1/user-agreements/${id}`;
        await apiClient.delete(url);
        // Optimistically update local state
        setAgreements((prev) => prev.filter((a) => a.id !== id));
        fetchAgreements();
        return true;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setError(errorMessage);
        throw err;
      }
    },
    [fetchAgreements]
  );

  const getAgreementById = useCallback(async (id) => {
    if (!id) {
      throw new Error("Agreement ID is required.");
    }
    try {
      const url = `profile-service/api/v1/user-agreements/${id}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error(err.response?.data);
      throw err;
    }
  }, []);

  useEffect(() => {
    fetchAgreements();
  }, [fetchAgreements]);

  return {
    agreements,
    loading,
    error,
    refetch: fetchAgreements,
    addAgreement,
    updateAgreement,
    deleteAgreement,
    getAgreementById,
  };
};

export default useUserAgreements;
